import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, TextField, Dialog, DialogContent, Avatar, IconButton, Card,
    Badge, TablePagination, MenuItem, FormControl, Select,
    Snackbar, Alert
} from '@mui/material';
import { Search as SearchIcon, Refresh, Visibility, Close as CloseIcon } from '@mui/icons-material';
import { getData } from '../../../services/axiosInstance';
import { API_ENDPOINTS } from '../../../services/endpoints';
import Loader from '../../../common/spinner';
import { formatDate } from '../../../utils/dateFormat';
import ViewLeaveRequest from '../../manager/leaveManagement/viewLeaveRequest';

const leaveTypeColors = {
    'Sick Leave': '#f44336',
    'Casual Leave': '#2196f3',
    'Work From Home': '#4caf50',
    'Loss Of Pay': '#9e9e9e'
};

function EmployeeLeaveRequestManager() {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    const orgId = userData?.organization_id;

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allLeaveTypes, setAllLeaveTypes] = useState([]);

    // Filters and Search
    const [filters, setFilters] = useState({ search: '', type: '', page: 0, limit: 10, total: 0 });
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Dialog and Snackbar State
    const [details, setDetails] = useState({ open: false, data: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // 1. Debounce Search Logic
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedSearch(filters.search), 500);
        return () => clearTimeout(handler);
    }, [filters.search]);

    // 2. Fetch Static Leave Types
    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const res = await getData(API_ENDPOINTS.fetchLeaveCountsForOrganization(orgId));
                if (res.status === 'SUCCESS') setAllLeaveTypes(res.message.leaveTypes || []);
            } catch (e) { return null; }
        };
        if (orgId) fetchTypes();
    }, [orgId]);

    // 3. Fetch Leave Requests
    const fetchLeaveRequests = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                organization_id: orgId,
                page: filters.page + 1,
                limit: filters.limit,
                ...(filters.type && { leave_type_id: filters.type }),
                ...(debouncedSearch.length >= 2 && { name: debouncedSearch })
            });

            const response = await getData(`${API_ENDPOINTS.getAllLeaveRequestsByOrganizationId({})}?${params}`);
            if (response.status === "SUCCESS") {
                const rawData = response.result?.data || [];
                const transformed = rawData.map(req => ({
                    ...req,
                    id: req.leave_id,
                    employeeName: req.employee_name,
                    avatar: req.employee_name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U',
                    fromDate: formatDate(req.start_date),
                    toDate: formatDate(req.end_date)
                }));
                setRequests(transformed);
                setFilters(prev => ({ ...prev, total: response.result?.pagination?.totalRecords || 0 }));
            }
        } catch (err) { return null; }
        setLoading(false);
    }, [orgId, filters.page, filters.limit, filters.type, debouncedSearch]);

    useEffect(() => {
        fetchLeaveRequests();
    }, [fetchLeaveRequests]);

    // Handlers
    const handleOpenDetails = (request) => {
        setDetails({ open: true, data: request });
    };

    const handleCloseDialog = () => {
        setDetails({ open: false, data: null });
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ p: 1 }}>
            {loading && <Loader />}

            {/* Header Card */}
            <Card elevation={0} sx={{ mb: 3, p: 2, background: 'linear-gradient(135deg, #6b8df2 0%, #4a6cf7 100%)', color: 'white', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>Leave Requests</Typography>
                        <Typography variant="body2">Manage employee leaves efficiently</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Badge badgeContent={filters.total} color="error">
                            <Card sx={{ px: 3, py: 1, borderRadius: 4 }}>
                                <Typography variant="subtitle2" color="primary" fontWeight={600}>Total Requests</Typography>
                            </Card>
                        </Badge>
                        <IconButton sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f0f0f0' } }} onClick={fetchLeaveRequests}>
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Card>

            {/* Filters and Table */}
            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" fontWeight={600}>Requests List</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            placeholder="Search Name..."
                            size="small"
                            value={filters.search}
                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 0 }))}
                            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} /> }}
                        />
                        <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                            <Select
                                displayEmpty
                                value={filters.type}
                                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value, page: 0 }))}
                                sx={{ borderRadius: 3 }}
                            >
                                <MenuItem value=""><em>All Leave Types</em></MenuItem>
                                {allLeaveTypes.map(type => <MenuItem key={type.id} value={type.id}>{type.leave_type}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                {['Employee', 'Department', 'Type', 'Duration', 'Days', 'Status', 'Action'].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.map((req) => (
                                <TableRow key={req.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Avatar sx={{ bgcolor: leaveTypeColors[req.leave_type] || '#9e9e9e', width: 36, height: 36, fontSize: 14 }}>
                                                {req.avatar}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>{req.employeeName}</Typography>
                                                <Typography variant="caption" color="textSecondary">{req.employee_id}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{req.department || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={req.leave_type}
                                            size="small"
                                            sx={{
                                                bgcolor: `${leaveTypeColors[req.leave_type] || '#ccc'}15`,
                                                color: leaveTypeColors[req.leave_type] || '#757575',
                                                borderRadius: 5, px: 1, textTransform: 'capitalize', width: '120px'
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell><Typography variant="caption">{req.fromDate} to {req.toDate}</Typography></TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{req.leave_duration}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={req.status}
                                            size="small"
                                            color={req.status === 'APPROVED' ? 'success' : req.status === 'REJECTED' ? 'error' : 'warning'}
                                            sx={{ fontWeight: 600, borderRadius: 5, px: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => handleOpenDetails(req)}>
                                            <Visibility sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={filters.total}
                    rowsPerPage={filters.limit}
                    page={filters.page}
                    onPageChange={(_, p) => setFilters(prev => ({ ...prev, page: p }))}
                    onRowsPerPageChange={(e) => setFilters(prev => ({ ...prev, limit: +e.target.value, page: 0 }))}
                />
            </Card>

            {/* View Details Dialog */}
            <Dialog
                open={details.open}
                onClose={handleCloseDialog}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" fontWeight="700">Leave Request Details</Typography>
                    <IconButton onClick={handleCloseDialog}><CloseIcon /></IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    {details.data && (
                        <ViewLeaveRequest
                            data={details.data}
                            onClose={handleCloseDialog}
                            refresh={fetchLeaveRequests}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

export default EmployeeLeaveRequestManager;